import { useMemo, useState } from 'react'
import { Check } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlowButton } from '@/components/ui/GlowButton'
import { InquiryModal } from '@/components/ui/InquiryModal'
import { GRAFIKA_ITEMS } from '@/lib/pricing'
import { iconForGrafika } from './icons'

/**
 * Graphic design multi-select "cart" — not a calculator, ported from the
 * live site's `GRAFIKA_ITEMS` + `renderGrafika()`. Selecting `logo` shows a
 * specific discount note pointing at the web calculator; selecting anything
 * else shows a generic combo note — same branching as the source.
 *
 * `filterIds` (optional): restrict the grid to a subset of GRAFIKA_ITEMS by
 * id, preserving GRAFIKA_ITEMS' own order. Added for the Print materijali
 * SEO landing page, which reuses this component but must not duplicate the
 * exact same 11-item grid already shown in full on the Graphic design page.
 * Omit for the original full-grid behaviour.
 */
export function GraphicDesignPanel({ filterIds }: { filterIds?: string[] } = {}) {
  const { t, lang } = useLanguage()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [inquiryOpen, setInquiryOpen] = useState(false)

  const items = useMemo(
    () => (filterIds ? GRAFIKA_ITEMS.filter((item) => filterIds.includes(item.id)) : GRAFIKA_ITEMS),
    [filterIds],
  )

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const total = useMemo(
    () => items.reduce((sum, item) => (selected.has(item.id) ? sum + item.price : sum), 0),
    [selected, items],
  )

  const hasLogo = selected.has('logo')
  const hasAny = selected.size > 0

  const selectedNames = items.filter((i) => selected.has(i.id)).map((i) => (lang === 'en' ? i.nameEn : i.name))
  const summaryLines = [
    `${t('tab-grafika')}:`,
    ...selectedNames.map((n) => `+ ${n}`),
    `${t('grafika-total')}: ${t('grafika-from')} ${total} EUR`,
  ]

  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-2xl text-sm leading-relaxed text-foreground-muted">{t('grafika-intro')}</p>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {items.map((item) => {
          const isSelected = selected.has(item.id)
          const Icon = iconForGrafika(item.id)
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => toggle(item.id)}
              className={`relative flex flex-col items-start gap-3 rounded-lg border p-4 text-left transition-all duration-normal ${
                isSelected
                  ? 'glow-ring border-accent-fire/50 bg-accent-fire/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
              }`}
            >
              <div className="flex w-full items-start justify-between">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors ${
                    isSelected ? 'border-accent-fire bg-accent-fire/20 text-accent-fire' : 'border-white/15 bg-white/5 text-foreground-muted'
                  }`}
                >
                  <Icon size={17} strokeWidth={2} />
                </div>
                <div
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
                    isSelected ? 'border-accent-fire bg-accent-fire/20' : 'border-white/25'
                  }`}
                >
                  <Check
                    size={14}
                    strokeWidth={3}
                    className={`text-accent-fire transition-transform duration-fast ${isSelected ? 'scale-100' : 'scale-0'}`}
                  />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">{lang === 'en' ? item.nameEn : item.name}</h4>
                <p className="mt-1 text-xs leading-relaxed text-foreground-muted">
                  {lang === 'en' ? item.descEn : item.desc}
                </p>
              </div>
              <span className="mt-auto font-mono text-xs font-bold text-accent-amber-light">
                {t('grafika-from')} {item.price} EUR
              </span>
            </button>
          )
        })}
      </div>

      {hasAny && (
        <div className="rounded-lg border border-accent-amber/30 bg-accent-amber/5 px-4 py-3 text-xs leading-relaxed text-foreground-muted">
          {hasLogo ? (
            <>
              {t('grafika-logo-discount-pre')}{' '}
              <strong className="text-accent-amber-light">{t('grafika-logo-discount-mid')}</strong>{' '}
              {t('grafika-logo-discount-post')}
            </>
          ) : (
            t('grafika-combo-note')
          )}
        </div>
      )}

      {hasAny && (
        <GlassCard className="flex flex-col items-center justify-between gap-4 p-5 sm:flex-row">
          <div>
            <div className="text-sm text-foreground-muted">
              {t('grafika-selected')}: <span className="font-semibold text-foreground">{selected.size}</span>
            </div>
            <div className="text-xs text-foreground-subtle">
              {t('grafika-total')}:{' '}
              <strong className="text-accent-amber-light">
                {t('grafika-from')} {total} EUR
              </strong>
            </div>
          </div>
          <GlowButton type="button" onClick={() => setInquiryOpen(true)}>
            {t('calc-cta')}
          </GlowButton>
        </GlassCard>
      )}

      <InquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} summary={summaryLines} />
    </div>
  )
}
